'use client'

import { useEffect, useState } from 'react'
import { pfetch } from '@/lib/phishtrainer/play-api'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { EmailBodyPreview } from '@/components/phishtrainer/email-body-preview'
import { isUnscorableRound } from '@/lib/phishtrainer/session-summary'
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Lightbulb,
  Mail,
  MessageSquareText,
  Minus,
  RotateCcw,
  XCircle,
} from 'lucide-react'
import type { EnrichedRound } from '@/app/api/phishtrainer/users/[id]/library/route'
import { useTranslation } from '@/lib/i18n'
import { DATE_TIME, formatLearnerDate } from '@/components/user-portal/learner-i18n'

const outcomeConfig: Record<string, { bg: string; labelKey: string; ring: string }> = {
  passed: { bg: 'bg-success/10 text-success border-success/20', labelKey: 'play.library.outcome.passed', ring: 'ring-success/30' },
  partial: { bg: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20', labelKey: 'play.library.outcome.partial', ring: 'ring-amber-500/30' },
  failed: { bg: 'bg-destructive/10 text-destructive border-destructive/20', labelKey: 'play.library.outcome.failed', ring: 'ring-destructive/30' },
}

// The cue a learner judged, named in their language (play.library.detail.signals.*).
const SIGNALS = new Set([
  'sender_name',
  'sender_email',
  'subject',
  'greeting',
  'spelling',
  'link',
  'attachment',
  'psych_manipulation',
])

/**
 * `null` when the round judged no elements — a gate-only Expert round is stored
 * 0/0 and decided at the inbox call. Returning 0 put a text-4xl red "0/0
 * correct" inside a green success hero for a learner who was RIGHT. Shared
 * predicate, so this drawer cannot contradict the row that opened it.
 */
function scorePct(correct: number, total: number): number | null {
  return isUnscorableRound({ correctCount: correct, totalCount: total })
    ? null
    : Math.round((correct / total) * 100)
}

function scoreColor(pct: number | null) {
  if (pct === null) return 'text-muted-foreground'
  if (pct >= 70) return 'text-success'
  if (pct >= 40) return 'text-amber-600 dark:text-amber-400'
  return 'text-destructive'
}

interface RoundDetailSheetProps {
  item: EnrichedRound | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RoundDetailSheet({ item, open, onOpenChange }: RoundDetailSheetProps) {
  /**
   * Replay is a PLAY-REALM action, and this drawer is shared with the ADMIN one.
   *
   * The button links to an absolute `/play/game?attackId=…`. That is correct for
   * a learner reading their own history, and wrong twice over for an operator
   * reviewing someone else's: this same component is mounted under
   * /phishtrainer/users/<id> via RecentRoundsCard, and the "Play as this user"
   * button sits two cards below it. With no play session the operator is ejected
   * out of the admin shell to the learner token gate; WITH one they are dropped
   * into a live round played AS that learner, and finishing it writes a real
   * round row, points, streak, tier and pillar-EWMA movement onto the record
   * they were only supposed to be reading. A review action silently rewrote the
   * learner's training history.
   *
   * The realm is derived from the pathname rather than threaded as a prop
   * because the pathname IS the predicate — the link is only ever valid when the
   * viewer is already inside /play — and a prop would have to be passed
   * correctly by every present and future caller of a shared component. That is
   * exactly the mistake PillarMasteryStrip's hardcoded /play href already made.
   */
  const pathname = usePathname()
  const inPlayRealm = pathname === '/play' || (pathname ?? '').startsWith('/play/')
  const { t, locale } = useTranslation()

  const [emailBody, setEmailBody] = useState<{ greeting?: string; bodyHtml?: string } | null>(null)
  const [loadingEmail, setLoadingEmail] = useState(false)
  /**
   * WHY this exists rather than one nullable body: every failure mode collapsed
   * into the same eight words, "Email content not available."
   *
   * The fetch never checked `r.ok`, so a 400 (the BOLA guard — the round is not
   * this learner's), a 401 (expired play session), a 404 or a 502 all had their
   * ERROR body parsed as if it were the round view, yielding `undefined` and
   * rendering the empty state. `.catch(() => setEmailBody(null))` did the same
   * for a network failure. Nothing was logged, so neither the learner nor an
   * operator could tell an expired session from a genuinely bodiless round —
   * which is exactly the position we were in when this was reported.
   */
  const [emailError, setEmailError] = useState<
    null | 'expired' | 'forbidden' | 'unavailable'
  >(null)

  useEffect(() => {
    if (!open || !item?.round?.id) {
      setEmailBody(null)
      return
    }
    setLoadingEmail(true)
    // Read the email from the PLAY realm, keyed on the ROUND — not from
    // /api/phishtrainer/safe-attacks/:id, which is an admin-realm route built on
    // adminFetch. A learner has no admin session, so that call always failed and
    // this panel was blank for every learner. The play endpoint also returns the
    // PERSONALIZED body actually served to them ({{company}} resolved), where the
    // raw SafeAttack row still carries the unresolved tokens.
    setEmailError(null)
    // Realm-agnostic: users/:id/rounds/:roundId tries the PLAY realm first (a
    // learner reading their own round) and falls back to the ADMIN realm (an
    // admin reviewing a learner). The old `/api/play/rounds/:id` authenticated
    // as the learner only, so this drawer showed "Email content not available"
    // for every round in the admin area.
    pfetch(`/api/phishtrainer/users/${item.round.userId}/rounds/${item.round.id}`)
      .then(async (r) => {
        if (!r.ok) {
          // Distinguish the cases instead of flattening them. 401 is a session
          // the learner can fix by signing in again; 400 is the BOLA guard and
          // means this round is not theirs; anything else is a real fault.
          const kind =
            r.status === 401 ? 'expired' : r.status === 400 ? 'forbidden' : 'unavailable'
          console.error(
            `[round-detail] email fetch failed: HTTP ${r.status} for round ${item.round.id}`,
          )
          setEmailError(kind)
          setEmailBody(null)
          return
        }
        const j = await r.json()
        const email = j?.data?.email
        if (!email) {
          // A 200 with no email is a CONTRACT problem, not a missing round —
          // worth its own log line so it is never confused with the above.
          console.error(
            `[round-detail] round ${item.round.id} returned 200 with no email payload`,
          )
          setEmailError('unavailable')
        }
        setEmailBody(
          email ? { greeting: email.greeting, bodyHtml: email.bodyHtml } : null,
        )
      })
      .catch((err: unknown) => {
        console.error(`[round-detail] email fetch threw for round ${item.round.id}:`, err)
        setEmailError('unavailable')
        setEmailBody(null)
      })
      .finally(() => setLoadingEmail(false))
  }, [open, item?.round?.id])

  if (!item) return null
  const { round, attack, analysis } = item
  const outcome = outcomeConfig[round.outcome] || outcomeConfig.failed
  const pct = scorePct(round.correctCount, round.totalCount)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full sm:max-w-2xl overflow-y-auto p-0"
        // Master–detail: clicking ANOTHER round row (outside the drawer) must
        // SWAP the drawer's content, not close it. Radix closes on the second
        // click's pointer-down-outside before the row's onClick can swap the
        // item, so the drawer flickered shut. Suppress the outside-close; the X
        // button and Escape still dismiss it.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Hero header with score */}
        <div className={`px-6 pt-6 pb-5 border-b ${round.outcome === 'passed' ? 'bg-success/5' : round.outcome === 'failed' ? 'bg-destructive/5' : 'bg-amber-500/5'}`}>
          <SheetHeader className="mb-4">
            <SheetTitle className="text-base leading-snug pe-8" dir="auto">
              {attack?.subject || t('play.library.detail.fallbackTitle')}
            </SheetTitle>
            {attack && (
              <SheetDescription className="text-xs">
                {t('play.library.detail.from', { name: attack.fromName ?? '', email: attack.fromEmail ?? '' })}
              </SheetDescription>
            )}
          </SheetHeader>
          <div className="flex items-center gap-4">
            <div className="flex items-baseline gap-1.5">
              {pct === null ? (
                <span className={`text-2xl font-bold ${scoreColor(pct)}`}>
                  {t('play.library.detail.decidedAtInbox')}
                </span>
              ) : (
                <>
                  <span className={`text-4xl font-bold tabular-nums ${scoreColor(pct)}`}>
                    {round.correctCount}/{round.totalCount}
                  </span>
                  <span className="text-sm text-muted-foreground">{t('play.library.detail.correct')}</span>
                </>
              )}
            </div>
            <Badge className={`${outcome.bg} border`}>
              {t(outcome.labelKey)}
            </Badge>
            <div className="flex items-center gap-2 ms-auto">
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatLearnerDate(round.completedAt || round.startedAt, locale, DATE_TIME)}
              </span>
              {inPlayRealm && (
                <Button variant="outline" size="sm" asChild className="text-xs">
                  <Link href={`/play/game?attackId=${round.safeAttackId}`}>
                    <RotateCcw className="w-3 h-3 me-1" />
                    {t('play.library.detail.replay')}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* AI Coaching — the most valuable insight, lead with it */}
          {analysis?.coachingMessage && (
            <div className="rounded-xl bg-primary/5 border border-primary/10 p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquareText className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold text-primary uppercase tracking-wide">{t('play.library.detail.coaching')}</span>
              </div>
              <ul className="space-y-2">
                {analysis.coachingMessage.split('\n').filter(Boolean).map((line, i) => {
                  const pillarMatch = line.match(/^\[([^\]]+)\]\s*(.*)$/)
                  return (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                      {pillarMatch ? (
                        <>
                          <span className="shrink-0 mt-0.5 rounded-md bg-primary/10 text-primary font-bold px-2 py-0.5 text-xs">
                            {pillarMatch[1]}
                          </span>
                          <span className="text-foreground">{pillarMatch[2]}</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground italic ps-1">{line}</span>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Original Email — shown prominently as a realistic email card */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">{t('play.library.detail.originalEmail')}</h4>
            </div>
            <div className="rounded-xl border bg-white dark:bg-card overflow-hidden shadow-sm">
              {/* Email chrome header */}
              <div className="px-4 py-3 border-b bg-muted/30 space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground font-medium w-12">{t('play.library.detail.fromLabel')}</span>
                  <bdi className="text-foreground">{attack?.fromName} &lt;{attack?.fromEmail}&gt;</bdi>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground font-medium w-12">{t('play.library.detail.subjectLabel')}</span>
                  <bdi className="text-foreground font-medium">{attack?.subject}</bdi>
                </div>
              </div>
              {/* Email body */}
              <div className="px-5 py-4">
                {loadingEmail ? (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : emailBody ? (
                  // Inert like every preview (owner, 2026-10-06): the review
                  // is not the exercise, and a click would leave for the lure's
                  // own domain. Hover still names where the link points.
                  <EmailBodyPreview greeting={emailBody.greeting} bodyHtml={emailBody.bodyHtml} />
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    {emailError === 'expired'
                      ? t('play.library.detail.expired')
                      : emailError === 'forbidden'
                        ? t('play.library.detail.forbidden')
                        : t('play.library.detail.unavailable')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Your answers — clean table-style layout */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">{t('play.library.detail.yourAnswers')}</h4>
            {pct === null ? (
              /* No element was ever judged. An empty bordered table read as "we
                 recorded nothing of what you did"; say what actually happened. */
              <p className="text-sm text-muted-foreground italic">
                {t('play.library.detail.gateOnlyAnswers')}
              </p>
            ) : (
            <div className="rounded-xl border overflow-hidden divide-y divide-border">
              {round.answers.map((a, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-4 py-2.5 text-sm"
                >
                  <div className="flex items-center gap-2.5">
                    {a.isCorrect ? (
                      <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-destructive shrink-0" />
                    )}
                    <span className="font-medium">
                      {SIGNALS.has(a.playerSignal) ? t(`play.library.detail.signals.${a.playerSignal}`) : a.playerSignal}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-muted-foreground">
                      {t('play.library.detail.you')}{' '}
                      <span className="font-medium text-foreground">
                        {a.userMarkedSuspicious ? t('play.library.detail.unsafe') : t('play.library.detail.safe')}
                      </span>
                    </span>
                    <span className={a.isCorrect ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                      {a.isCorrect ? t('play.library.detail.right') : t('play.library.detail.wrong')}
                    </span>
                    {/* The sign comes from the VALUE, never from a hardcoded '+'.
                        scoreRound writes a NEGATIVE pointsAwarded for a false
                        positive (marking a safe element unsafe) and the library
                        proxy passes it straight through, so a hardcoded plus
                        rendered the one row that COST the learner points as
                        "+-5" — and clipped inside a 24px cell at that. Three
                        characters have to fit, hence w-10. */}
                    <span className="text-muted-foreground tabular-nums w-10 text-end">
                      {a.pointsAwarded > 0 ? `+${a.pointsAwarded}` : a.pointsAwarded}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>

          {/* AI Analysis — pillar impact + flag details */}
          {analysis && !analysis.fallback && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">{t('play.library.detail.aiAnalysis')}</h4>
              <div className="space-y-4">
                {/* Pillar impact pills */}
                {analysis.perPillarRollup.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-2">{t('play.library.detail.pillarImpact')}</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.perPillarRollup.map((p) => (
                        <div
                          key={p.pillarCode}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium ${
                            p.masteryDelta >= 0
                              ? 'bg-success/5 border-success/20 text-success'
                              : 'bg-destructive/5 border-destructive/20 text-destructive'
                          }`}
                        >
                          <span>{p.pillarCode}</span>
                          {p.confidenceShift === 'up' && <ArrowUp className="w-3 h-3" />}
                          {p.confidenceShift === 'down' && <ArrowDown className="w-3 h-3" />}
                          {p.confidenceShift === 'stable' && <Minus className="w-3 h-3" />}
                          <span>
                            {p.masteryDelta >= 0 ? '+' : ''}{Math.round(p.masteryDelta * 100)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-flag assessments */}
                {analysis.perFlagAssessment.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-2">{t('play.library.detail.flagAssessment')}</p>
                    <div className="rounded-xl border overflow-hidden divide-y divide-border">
                      {analysis.perFlagAssessment.map((f, i) => (
                        <div key={i} className="px-4 py-3 space-y-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {f.userDetected === f.expectedDetection ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                              ) : (
                                <XCircle className="w-3.5 h-3.5 text-destructive" />
                              )}
                              <span className="text-sm font-medium">{f.flagCode}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              <span className={f.userDetected === f.expectedDetection ? 'text-success' : 'text-destructive'}>
                                {f.userDetected === f.expectedDetection
                                  ? t('play.library.detail.detected')
                                  : t('play.library.detail.missed')}
                              </span>
                              {/* Nuance score bar */}
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${f.nuanceScore >= 0.7 ? 'bg-success' : f.nuanceScore >= 0.4 ? 'bg-amber-500' : 'bg-destructive'}`}
                                    style={{ width: `${Math.round(f.nuanceScore * 100)}%` }}
                                  />
                                </div>
                                <span className="text-muted-foreground tabular-nums">{Math.round(f.nuanceScore * 100)}%</span>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground ps-5.5 leading-relaxed">{f.reasoning}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested focus */}
                {analysis.suggestedNextFocus.length > 0 && (
                  <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                    <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5">{t('play.library.detail.focusAreas')}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {analysis.suggestedNextFocus.map((f) => (
                          <Badge key={f} variant="outline" className="text-xs bg-white dark:bg-card">
                            {f}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {analysis?.fallback && (
            <p className="text-xs text-muted-foreground italic px-1">
              {t('play.library.detail.basicAssessment')}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
